// Adaptive Configuration Engine
// Generates routing profiles, savings estimates, and personalization based on platform selection

import { deriveConfiguration, getPlatformById } from "@/data/platforms";

// ── Routing Profiles ──

const ROUTING_PROFILES = {
  openai_only: {
    name: "OpenAI Optimized",
    description: "Shifts simple tasks from GPT-4o to GPT-4o-mini for major savings.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gpt-5.2",     reason: "Complex reasoning needs the most capable model" },
      { taskComplexity: "complex",  assignedModel: "gpt-4o",      reason: "Strong general-purpose for multi-step tasks" },
      { taskComplexity: "moderate", assignedModel: "gpt-4o",      reason: "Reliable for standard business tasks" },
      { taskComplexity: "simple",   assignedModel: "gpt-4o-mini", reason: "Simple tasks don't need full GPT-4o — 90% more cost-effective" },
      { taskComplexity: "trivial",  assignedModel: "gpt-4o-mini", reason: "Classification/extraction at lowest cost" },
    ],
    savingsRange: [30, 40],
  },
  openai_google: {
    name: "OpenAI + Google Optimized",
    description: "Routes bulk work to Gemini Flash for 33x cost reduction on simple tasks.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gpt-5.2",       reason: "Complex reasoning stays on GPT-5.2" },
      { taskComplexity: "complex",  assignedModel: "gpt-4o",        reason: "Multi-step tasks on GPT-4o" },
      { taskComplexity: "moderate", assignedModel: "gemini-3-pro",  reason: "Standard tasks shift to Gemini Pro — 40% more cost-effective than GPT-4o" },
      { taskComplexity: "simple",   assignedModel: "gemini-3-flash", reason: "Simple tasks on Gemini Flash — 95% more cost-effective than GPT-4o" },
      { taskComplexity: "trivial",  assignedModel: "gemini-3-flash", reason: "Most cost-effective option for trivial tasks" },
    ],
    savingsRange: [40, 55],
  },
  openai_anthropic: {
    name: "OpenAI + Anthropic Optimized",
    description: "Uses Claude for code tasks, GPT-5.2 for reasoning, Haiku for simple work.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gpt-5.2",        reason: "Most capable for complex reasoning" },
      { taskComplexity: "complex",  assignedModel: "claude-sonnet-4", reason: "Claude excels at code generation and analysis" },
      { taskComplexity: "moderate", assignedModel: "gpt-4o",         reason: "General-purpose at reasonable cost" },
      { taskComplexity: "simple",   assignedModel: "claude-haiku-4",  reason: "Claude Haiku for simple tasks — very cost-effective" },
      { taskComplexity: "trivial",  assignedModel: "claude-haiku-4",  reason: "Most cost-effective Anthropic option" },
    ],
    savingsRange: [35, 45],
  },
  anthropic_only: {
    name: "Anthropic Optimized",
    description: "Routes simple tasks to Claude Haiku, complex work stays on Sonnet.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "claude-sonnet-4", reason: "Best Claude model for complex tasks" },
      { taskComplexity: "complex",  assignedModel: "claude-sonnet-4", reason: "Sonnet handles multi-step analysis well" },
      { taskComplexity: "moderate", assignedModel: "claude-haiku-4",  reason: "Haiku handles standard tasks at lower cost" },
      { taskComplexity: "simple",   assignedModel: "claude-haiku-4",  reason: "Simple tasks routed to most cost-effective tier" },
      { taskComplexity: "trivial",  assignedModel: "claude-haiku-4",  reason: "Classification at minimal cost" },
    ],
    savingsRange: [25, 35],
  },
  google_only: {
    name: "Google AI Optimized",
    description: "Routes everything between Gemini Pro and Flash for optimal cost.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gemini-3-pro",   reason: "Best Gemini model for complex reasoning" },
      { taskComplexity: "complex",  assignedModel: "gemini-3-pro",   reason: "Pro handles multi-step tasks well" },
      { taskComplexity: "moderate", assignedModel: "gemini-3-flash", reason: "Flash handles standard tasks at fraction of cost" },
      { taskComplexity: "simple",   assignedModel: "gemini-3-flash", reason: "Simple tasks on most cost-effective model" },
      { taskComplexity: "trivial",  assignedModel: "gemini-3-flash", reason: "Near-zero cost for trivial tasks" },
    ],
    savingsRange: [35, 50],
  },
  multi_provider: {
    name: "Full Multi-Provider",
    description: "Optimal model for every complexity level across all providers.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gpt-5.2",        reason: "Best-in-class reasoning" },
      { taskComplexity: "complex",  assignedModel: "claude-sonnet-4", reason: "Best for code and structured analysis" },
      { taskComplexity: "moderate", assignedModel: "gemini-3-pro",   reason: "Cost-effective general purpose" },
      { taskComplexity: "simple",   assignedModel: "gemini-3-flash", reason: "Optimal for simple tasks" },
      { taskComplexity: "trivial",  assignedModel: "gpt-4o-mini",   reason: "Fast classification and extraction" },
    ],
    savingsRange: [45, 60],
  },
  regional: {
    name: "Regional Optimized",
    description: "Includes regional models for competitive pricing and local compliance.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "gpt-5.2",      reason: "Global best for complex reasoning" },
      { taskComplexity: "complex",  assignedModel: "deepseek-v3",  reason: "DeepSeek excels at reasoning tasks" },
      { taskComplexity: "moderate", assignedModel: "qwen-max",     reason: "Strong general purpose, competitive pricing" },
      { taskComplexity: "simple",   assignedModel: "gemini-3-flash", reason: "Most cost-effective global option" },
      { taskComplexity: "trivial",  assignedModel: "gemini-3-flash", reason: "Near-zero cost" },
    ],
    savingsRange: [50, 65],
  },
  anthropic_google: {
    name: "Anthropic + Google Optimized",
    description: "Claude Sonnet for code, Gemini Flash for bulk — great balance.",
    rules: [
      { taskComplexity: "expert",   assignedModel: "claude-sonnet-4", reason: "Claude excels at complex reasoning and code" },
      { taskComplexity: "complex",  assignedModel: "claude-sonnet-4", reason: "Sonnet for multi-step analysis" },
      { taskComplexity: "moderate", assignedModel: "gemini-3-pro",   reason: "Gemini Pro for standard tasks" },
      { taskComplexity: "simple",   assignedModel: "gemini-3-flash", reason: "Flash for simple tasks — 95% more cost-effective" },
      { taskComplexity: "trivial",  assignedModel: "claude-haiku-4",  reason: "Haiku for trivial classification" },
    ],
    savingsRange: [40, 55],
  },
};

// Model costs per 1K tokens (input)
const MODEL_COSTS = {
  "gpt-5.2": 0.015, "gpt-4o": 0.0025, "gpt-4o-mini": 0.00015,
  "claude-sonnet-4": 0.003, "claude-haiku-4": 0.00025,
  "gemini-3-pro": 0.00125, "gemini-3-flash": 0.000075,
  "grok-3": 0.005, "llama-4": 0.001, "mistral-large": 0.004, "mistral-medium": 0.0027,
  "deepseek-v3": 0.0014, "qwen-max": 0.002,
};

// ── Profile Selection Logic ──

function selectRoutingProfile(providers) {
  const s = new Set(providers);
  const hasRegional = s.has("deepseek") || s.has("baidu") || s.has("alibaba");

  if (providers.length >= 3) return ROUTING_PROFILES.multi_provider;
  if (hasRegional) return ROUTING_PROFILES.regional;
  if (s.has("openai") && s.has("google")) return ROUTING_PROFILES.openai_google;
  if (s.has("openai") && s.has("anthropic")) return ROUTING_PROFILES.openai_anthropic;
  if (s.has("anthropic") && s.has("google")) return ROUTING_PROFILES.anthropic_google;
  if (s.has("openai")) return ROUTING_PROFILES.openai_only;
  if (s.has("anthropic")) return ROUTING_PROFILES.anthropic_only;
  if (s.has("google")) return ROUTING_PROFILES.google_only;
  return ROUTING_PROFILES.multi_provider;
}

// ── Savings Estimation ──

export function estimateSavings(providers, agentCount = 10) {
  const profile = selectRoutingProfile(providers);
  const [minSavings, maxSavings] = profile.savingsRange;
  const midSavings = Math.round((minSavings + maxSavings) / 2);

  // Estimate monthly spend based on team size
  let monthlySpend;
  if (agentCount < 10) monthlySpend = 500;
  else if (agentCount < 100) monthlySpend = 5000;
  else if (agentCount < 1000) monthlySpend = 50000;
  else monthlySpend = 250000;

  const savingsDollar = Math.round(monthlySpend * midSavings / 100);

  const providerNames = providers.map(p => {
    const names = { openai: "ChatGPT", anthropic: "Claude", google: "Gemini", deepseek: "DeepSeek", meta: "Meta AI", xai: "Grok", mistral: "Mistral" };
    return names[p] || p;
  });

  const explanation = providerNames.length === 1
    ? `Based on your use of ${providerNames[0]}, QNNEAL shifts simple tasks to more cost-effective model tiers within the same provider.`
    : `By routing across ${providerNames.join(" + ")}, QNNEAL matches each task to the optimal model that meets quality requirements.`;

  return {
    currentMonthlyEstimate: monthlySpend,
    projectedSavingsPercent: midSavings,
    projectedSavingsDollar: savingsDollar,
    savingsRange: `${minSavings}-${maxSavings}%`,
    explanation,
  };
}

// ── Main Adaptive Config Builder ──

export function buildAdaptiveConfig(selectedPlatformIds) {
  const base = deriveConfiguration(selectedPlatformIds);
  const profile = selectRoutingProfile(base.providers);
  const savings = estimateSavings(base.providers);

  return {
    ...base,
    routingProfile: profile,
    savingsEstimate: savings,
    routingProfileId: Object.entries(ROUTING_PROFILES).find(([, v]) => v === profile)?.[0] || "multi_provider",
    configuredAt: new Date().toISOString(),
  };
}

// ── Helpers for Code Examples filtering ──

export function getRelevantExamplePlatforms(providers) {
  const platformMap = {
    openai: ["python", "javascript", "curl", "mcp"],
    anthropic: ["python", "javascript", "curl"],
    google: ["python", "javascript"],
  };
  const relevant = new Set();
  for (const p of providers) {
    (platformMap[p] || []).forEach(ep => relevant.add(ep));
  }
  // Always include common frameworks
  ["crewai", "autogen", "langgraph", "manus"].forEach(f => relevant.add(f));
  return [...relevant];
}

export { ROUTING_PROFILES, MODEL_COSTS, selectRoutingProfile };

// ── Platform Recommendation Engine ──

const RECOMMENDATION_RULES = [
  {
    id: "add_gemini_for_simple",
    condition: (providers) => providers.includes("openai") && !providers.includes("google"),
    platformId: "gemini",
    title: "Add Google Gemini",
    description: "Route simple tasks to Gemini Flash — 33x more cost-effective than GPT-4o with comparable quality.",
    extraSavings: 15,
    reason: "cost_reduction",
  },
  {
    id: "add_claude_for_code",
    condition: (providers) => !providers.includes("anthropic") && (providers.includes("openai") || providers.includes("google")),
    platformId: "claude",
    title: "Add Claude for code tasks",
    description: "Claude Sonnet excels at code generation and analysis. Teams with code tasks save 12% more.",
    extraSavings: 12,
    reason: "quality_boost",
  },
  {
    id: "add_openai_for_reasoning",
    condition: (providers) => !providers.includes("openai") && providers.includes("google"),
    platformId: "chatgpt",
    title: "Add ChatGPT for complex reasoning",
    description: "GPT-5.2 leads on multi-step reasoning. Add it for expert-level tasks while keeping Gemini for bulk work.",
    extraSavings: 10,
    reason: "quality_boost",
  },
  {
    id: "add_deepseek_regional",
    condition: (providers) => providers.length >= 1 && !providers.includes("deepseek") && providers.length < 4,
    platformId: "deepseek",
    title: "Consider DeepSeek for reasoning",
    description: "DeepSeek V3 handles complex reasoning at 60% less cost than GPT-4o. Popular with cost-conscious teams.",
    extraSavings: 18,
    reason: "cost_reduction",
  },
  {
    id: "go_multi_provider",
    condition: (providers) => providers.length === 1,
    platformId: providers => {
      if (providers.includes("openai")) return "gemini";
      if (providers.includes("google")) return "chatgpt";
      if (providers.includes("anthropic")) return "gemini";
      return "chatgpt";
    },
    title: "Go multi-provider for bigger savings",
    description: "Single-provider routing limits savings to 25-40%. Adding a second provider unlocks 40-55% savings.",
    extraSavings: 15,
    reason: "multi_provider",
  },
];

export function getRecommendations(providers, maxResults = 3) {
  if (!providers || providers.length === 0) return [];

  const recommendations = [];
  for (const rule of RECOMMENDATION_RULES) {
    if (recommendations.length >= maxResults) break;
    if (!rule.condition(providers)) continue;

    const platformId = typeof rule.platformId === "function" ? rule.platformId(providers) : rule.platformId;
    // Don't recommend a platform whose provider is already selected
    if (recommendations.some(r => r.platformId === platformId)) continue;

    recommendations.push({
      id: rule.id,
      platformId,
      title: rule.title,
      description: rule.description,
      extraSavings: rule.extraSavings,
      reason: rule.reason,
    });
  }
  return recommendations;
}

